import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePublishedPolicy from "../hooks/usePublishedPolicy";
import PolicyPage, { PolicyLoading } from "../components/PolicyText";
import {
  COMPANY_NAME,
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_HREF,
  EFFECTIVE_DATE,
  GRIEVANCE_OFFICER_NAME,
  JURISDICTION_CITY,
  LAST_UPDATED,
  REGISTERED_ADDRESS,
} from "../data/termsAndConditions";

function Clause({ number, children }: { number: string; children: ReactNode }) {
  return (
    <p>
      <span className="font-semibold text-ocean-950 dark:text-foam-50">{number}</span> {children}
    </p>
  );
}

interface SectionProps {
  id: string;
  number: number;
  title: string;
  delay?: number;
  children: ReactNode;
}

function Section({ id, number, title, delay = 0, children }: SectionProps) {
  return (
    <div id={id} className="scroll-mt-24">
      <ScrollReveal delay={delay}>
        <h2 className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">
          {number}. {title}
        </h2>
        <div className="mt-3 space-y-3 text-sm leading-relaxed text-ocean-800/70 dark:text-foam-100/70">
          {children}
        </div>
      </ScrollReveal>
    </div>
  );
}

/** The built-in text: shown until staff publish a version of the Terms in the admin panel (Policy & legal). */
function BuiltInTerms() {
  useDocumentTitle("Terms & Conditions");
  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={10} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Legal
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Terms &amp; Conditions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Effective Date: {EFFECTIVE_DATE} · Last Updated: {LAST_UPDATED}
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      <section className="mx-auto max-w-3xl space-y-14 px-6 py-24">
        <ScrollReveal className="text-sm leading-relaxed text-ocean-800/70 dark:text-foam-100/70">
          <p>
            These Terms and Conditions (&quot;Terms&quot;) are framed in accordance with the Indian Contract
            Act, 1872, the Sale of Goods Act, 1930, the Consumer Protection Act, 2019 and the Consumer
            Protection (E-Commerce) Rules, 2020, the Information Technology Act, 2000, the Food Safety and
            Standards Act, 2006 and regulations made by the Food Safety and Standards Authority of India
            (&quot;FSSAI&quot;), and the Legal Metrology Act, 2009 and the Legal Metrology (Packaged
            Commodities) Rules, 2011, as applicable to the sale of fresh seafood through e-commerce in India.
          </p>
        </ScrollReveal>

        <Section id="definitions" number={1} title="Definitions">
          <Clause number='1.1 "App/Platform"'>
            means the {COMPANY_NAME} mobile application and/or website through which the Supplier offers Goods
            and/or Services for sale.
          </Clause>
          <Clause number='1.2 "Buyer"'>
            means the individual or organisation who buys or agrees to buy the Goods and/or Services from the
            Supplier through the Platform.
          </Clause>
          <Clause number='1.3 "Consumer"'>
            has the meaning given to it under Section 2(7) of the Consumer Protection Act, 2019, and refers to
            a Buyer who purchases Goods or avails Services for personal use and not for resale or commercial
            purposes.
          </Clause>
          <Clause number='1.4 "Contract"'>
            means the contract between the Supplier and the Buyer for the sale and purchase of Goods and/or
            Services, incorporating these Terms.
          </Clause>
          <Clause number='1.5 "Goods"'>
            means the fresh seafood and related articles that the Buyer agrees to buy from the Supplier through
            the Platform.
          </Clause>
          <Clause number='1.6 "Services"'>means the delivery and related services that the Buyer agrees to buy from the Supplier.</Clause>
          <Clause number='1.7 "Supplier"'>
            means {COMPANY_NAME}, having its registered office at {REGISTERED_ADDRESS}, which owns and operates
            the Platform.
          </Clause>
          <Clause number='1.8 "Order Confirmation"'>
            means the Supplier&apos;s acceptance of a Buyer&apos;s order, communicated by email, SMS, push
            notification, or in-app confirmation.
          </Clause>
        </Section>

        <Section id="acceptance" number={2} title="Acceptance and Scope" delay={0.02}>
          <Clause number="2.1">
            Nothing in these Terms shall affect the Buyer&apos;s statutory rights as a Consumer under the
            Consumer Protection Act, 2019, or any other applicable law.
          </Clause>
          <Clause number="2.2">
            These Terms apply to all contracts for the sale of Goods and/or Services by the Supplier to the
            Buyer through the Platform and prevail over any other documentation or communication from the
            Buyer, except as expressly agreed in writing by the Supplier.
          </Clause>
          <Clause number="2.3">
            By placing an order on the Platform, the Buyer confirms that they have read, understood, and agree
            to be bound by these Terms and the{" "}
            <Link to="/privacy" className="font-semibold text-teal-600 hover:text-teal-700 dark:text-teal-300">
              {COMPANY_NAME} Privacy Policy
            </Link>
            , which is incorporated into these Terms by reference.
          </Clause>
          <Clause number="2.4">
            Any variation to these Terms (including any special terms agreed between the parties) is
            inapplicable unless agreed in writing by the Supplier.
          </Clause>
          <Clause number="2.5">These Terms may be updated from time to time in accordance with Section 15 (&quot;Amendments&quot;).</Clause>
        </Section>

        <Section id="eligibility" number={3} title="Eligibility" delay={0.02}>
          <Clause number="3.1">
            By using the Platform, the Buyer confirms that they are at least 18 years of age and competent to
            contract under the Indian Contract Act, 1872. The Platform is not intended for use by individuals
            under 18 without the involvement of a parent or guardian.
          </Clause>
          <Clause number="3.2">
            The Buyer is responsible for providing accurate registration, contact, and delivery information,
            and for maintaining the confidentiality of their account credentials.
          </Clause>
        </Section>

        <Section id="ordering" number={4} title="Ordering" delay={0.02}>
          <Clause number="4.1">
            All orders placed on the Platform constitute an offer by the Buyer to purchase Goods and/or
            Services on these Terms, subject to acceptance by the Supplier. The Supplier may decline to accept
            an order — for example, where the Buyer&apos;s delivery address falls outside the serviceable area,
            where payment cannot be verified, or where fraud or abuse is suspected — and will notify the Buyer
            and refund any amount already collected.
          </Clause>
          <Clause number="4.2">
            Where Goods ordered by the Buyer are unavailable from the day&apos;s fresh catch, the Buyer will be
            notified before the order is confirmed or, if already confirmed, will be given the option to
            substitute the item, wait for the next available delivery date, or cancel that portion of the order
            for a full refund, credited within 4 business days to the original payment method (or as prescribed
            by RBI guidelines, whichever is sooner).
          </Clause>
          <Clause number="4.3">
            Promotional and discount codes may be used only once per Buyer, where applicable, and may not be
            combined with any other offer unless stated. If a code is used more than permitted, or is used in a
            manner the Supplier reasonably considers to abuse its fair-usage policy, the Supplier may withdraw,
            withhold, or cancel the promotional code and any order benefiting from it, and will notify the
            Buyer of the reason.
          </Clause>
        </Section>

        <Section id="price-and-payment" number={5} title="Price and Payment" delay={0.02}>
          <Clause number="5.1">
            The price of the Goods and/or Services is as stipulated on the Platform at the time of order,
            inclusive of applicable taxes unless stated otherwise.
          </Clause>
          <Clause number="5.2">
            The total price, including delivery charges (if any), will be displayed in the Buyer&apos;s cart
            before the order is confirmed.
          </Clause>
          <Clause number="5.3">
            On receiving the order, the Supplier will send an Order Confirmation by email/SMS/in-app
            notification, including the details, description, and price of the Goods and/or Services, and a
            link to these Terms and the applicable cancellation, return, and refund policy set out in Section
            10.
          </Clause>
          <Clause number="5.4">
            Payment of the price and delivery charges (if applicable) must be made at the time of ordering
            through the payment methods available on the Platform, processed by RBI-authorised third-party
            payment providers as described in the{" "}
            <Link to="/privacy" className="font-semibold text-teal-600 hover:text-teal-700 dark:text-teal-300">
              Privacy Policy
            </Link>
            .
          </Clause>
          <Clause number="5.5">
            For orders placed in advance, payment must be made at the time of ordering, although Goods will not
            be dispatched until the agreed delivery date.
          </Clause>
          <Clause number="5.6">
            If a payment is not made on time, is rejected, or is reversed, the amount owing will be treated as
            overdue and the Supplier may suspend the relevant order, Service, or further deliveries until
            payment is received.
          </Clause>
        </Section>

        <Section id="product-information" number={6} title="Product Information, Sourcing, and Food Safety" delay={0.02}>
          <Clause number="6.1 Sourcing and country of origin.">
            {COMPANY_NAME}&apos;s seafood is fresh catch sourced daily from local fishing harbours in India,
            unless a specific product listing states otherwise (for example, farmed/aquaculture or imported
            items, which will be labelled with their country of origin as required under the Consumer
            Protection (E-Commerce) Rules, 2020).
          </Clause>
          <Clause number="6.2 FSSAI licence.">
            {COMPANY_NAME}&apos;s FSSAI licence/registration is in progress. Once issued, the licence number
            will be displayed on the Platform and on delivery packaging, in accordance with the Food Safety and
            Standards Act, 2006 and applicable FSSAI regulations for e-commerce food business operators — see
            our{" "}
            <Link to="/legal" className="font-semibold text-teal-600 hover:text-teal-700 dark:text-teal-300">
              Legal &amp; Registrations
            </Link>{" "}
            page for current status.
          </Clause>
          <Clause number="6.3 Weight and quantity.">
            Net weight shown for Goods sold by weight reflects the weight of the edible/deliverable product and
            excludes ice, gel packs, and packaging, in accordance with the Legal Metrology (Packaged
            Commodities) Rules, 2011. Because seafood is a natural product, minor variation in weight, size,
            and cut between what is shown on the Platform and what is delivered may occur; where the variation
            is material, the Buyer may raise it under Section 9.8.
          </Clause>
          <Clause number="6.4 Product images and description.">
            Images are for illustration only. Actual appearance, cut, and portion size of fresh seafood may
            vary from the image shown.
          </Clause>
          <Clause number="6.5 Allergens and food safety.">
            Seafood and shellfish are recognised allergens. Buyers with food allergies or sensitivities should
            check product descriptions and, if in doubt, contact the Supplier before ordering. Buyers are
            responsible for the safe storage, refrigeration, and handling of Goods after delivery — see Section
            9.9.
          </Clause>
          <Clause number="6.6 Seasonal availability.">
            Availability of specific marine species may be affected by the annual government-mandated fishing
            ban period observed along the Indian coast (typically during the monsoon season), weather, and
            catch volumes. During such periods, {COMPANY_NAME} may offer aquaculture-farmed or alternative
            species, clearly indicated on the product listing, or may be unable to fulfil orders for certain
            items — see Section 10 (&quot;Force Majeure&quot;).
          </Clause>
        </Section>

        <Section id="performance-of-services" number={7} title="Performance of Services" delay={0.02}>
          <Clause number="7.1">
            The Supplier will perform the Services with reasonable skill and care. The Supplier does not
            guarantee that the Services will be uninterrupted or error-free, or that data generated, stored,
            transmitted, or used in connection with the Services will always be complete, accurate, or
            delivered without fault, though it will use reasonable efforts to remedy any such issues promptly.
          </Clause>
          <Clause number="7.2">
            The Supplier may need to suspend the Services for repair, maintenance, or improvement, and will
            restore them as quickly as reasonably possible, with advance notice where practicable.
          </Clause>
        </Section>

        <Section id="rights-of-supplier" number={8} title="Rights of the Supplier" delay={0.02}>
          <Clause number="8.1">
            The Supplier may periodically update prices on the Platform, which cannot be guaranteed for any
            period. The Supplier will make reasonable efforts to ensure prices are correct at the time the
            Buyer places an order; if a pricing error is identified after an order is placed, the Supplier will
            notify the Buyer and offer the option to proceed at the correct price or cancel for a full refund.
          </Clause>
          <Clause number="8.2">
            The Supplier may withdraw any Goods and/or Services from the Platform at any time, including where
            fresh stock is unavailable.
          </Clause>
          <Clause number="8.3">
            The Supplier may withdraw or suspend any promotion or offer code at any time, without affecting
            orders already confirmed.
          </Clause>
          <Clause number="8.4">
            The Supplier will not be liable for withdrawing Goods and/or Services from the Platform, or for
            declining to process an order, except for refunding any amount already collected for that order.
          </Clause>
        </Section>

        <Section id="delivery-cancellation-returns-refunds" number={9} title="Delivery, Cancellation, Returns, and Refunds" delay={0.02}>
          <Clause number="9.1">
            Where a specific delivery date has been agreed and cannot be met, the Supplier will notify the
            Buyer as soon as reasonably possible and offer the Buyer the choice of a new delivery date or a
            full refund.
          </Clause>
          <Clause number="9.2">
            The Supplier will use reasonable efforts to meet any agreed delivery date. Time of delivery is not
            of the essence, and the Supplier is not liable for losses, costs, damages, or expenses arising
            directly or indirectly from a failure to meet an estimated delivery time, except where caused by
            the Supplier&apos;s negligence.
          </Clause>
          <Clause number="9.3">
            Goods will be delivered to the address specified in the order, and the Buyer must make arrangements
            to receive delivery when tendered. Products are delivered between 10:00 a.m. and 1:00 p.m. on
            service days (Wednesday, Saturday, and Sunday), or as otherwise indicated on the Platform for the
            Buyer&apos;s location; these windows are indicative and may vary due to traffic, weather, or
            operational constraints.
          </Clause>
          <Clause number="9.4">
            Risk in the Goods passes to the Buyer on delivery, or, where the Buyer fails to take delivery at
            the agreed time without a valid reason, at the time delivery was attempted. Title in the Goods does
            not pass to the Buyer until payment has been made in full.
          </Clause>
          <Clause number="9.5">
            If a Buyer refuses delivery of a correctly fulfilled order for reasons not attributable to the
            Supplier (for example, being unavailable at the delivery address), the order is returned to the
            Supplier and, because seafood is perishable and cannot be resold, the Buyer will be charged the
            cost of the product and the return delivery, limited to the Supplier&apos;s reasonable and actual
            costs incurred, in accordance with Section 74 of the Indian Contract Act, 1872. Consistent with
            Rule 5(3)(l) of the Consumer Protection (E-Commerce) Rules, 2020, {COMPANY_NAME} will not levy a
            cancellation or refusal charge on a Buyer in circumstances where an equivalent charge would not
            apply if {COMPANY_NAME} itself cancelled the order for reasons within its control (see Section
            9.8).
          </Clause>
          <Clause number="9.6">
            If the delivery address provided by the Buyer is inaccurate, incomplete, or requires the delivery
            partner to travel more than 3 km beyond the originally specified location, additional delivery
            charges will apply, calculated as per the rate card displayed on the Platform at the time of
            delivery.
          </Clause>
          <Clause number="9.7 Cancellation before dispatch.">
            Because {COMPANY_NAME}&apos;s seafood is fresh and prepared to order, once an order has been
            confirmed and preparation has begun, it generally cannot be cancelled by the Buyer. If the Buyer
            wishes to cancel, they should contact {COMPANY_NAME} immediately — cancellation may be accepted, at
            the Supplier&apos;s discretion, only where the order has not yet been processed or dispatched. This
            clause does not affect the Buyer&apos;s statutory rights under Section 9.8 for defective, damaged,
            or incorrect Goods.
          </Clause>
          <Clause number="9.8 Quality issues, damaged, defective, or incorrect Goods.">
            If a delivered product is spoiled, damaged, defective, or different from what was ordered, the
            Buyer must contact {COMPANY_NAME} within 2 hours of delivery, with photographs where possible,
            before returning or discarding the Goods. On verification, the Buyer is entitled to a replacement
            or a full refund. This right is in addition to, and does not limit, the Buyer&apos;s rights under
            the Consumer Protection Act, 2019 in respect of defective goods or deficient services.
          </Clause>
          <Clause number="9.9">
            Where the Supplier reasonably requires the Goods to be returned to assess a claim, the Buyer must
            ensure the item is clearly labelled with the order number and kept refrigerated pending collection.
            Given the perishable nature of seafood, {COMPANY_NAME} will ordinarily resolve claims based on
            photographic or other evidence rather than requiring physical return. Buyers are responsible for
            refrigerating or otherwise safely storing Goods promptly on delivery; {COMPANY_NAME} is not
            responsible for spoilage caused by delay in storage after a successful delivery.
          </Clause>
          <Clause number="9.10">
            Orders placed a day in advance are subject to the availability of the specific product from the
            next day&apos;s fresh catch. As {COMPANY_NAME} sources fresh product daily and does not hold frozen
            stock of most items, an order (or part of an order) may need to be cancelled if that product is
            unavailable on the delivery date. Where this happens, the Buyer will be notified promptly and
            refunded in full for the affected item(s) within 4 business days.
          </Clause>
        </Section>

        <Section id="force-majeure" number={10} title="Force Majeure" delay={0.02}>
          <p>
            The Supplier will not be liable for any failure or delay in performing its obligations where this
            is caused by circumstances beyond its reasonable control, including adverse weather, rough seas,
            the annual government-mandated marine fishing ban, natural disasters, epidemic or pandemic,
            strikes, transport or fuel disruptions, power or internet outages, or changes in law or government
            action. Where such an event affects an order, the Supplier will notify the Buyer as soon as
            reasonably possible and offer a revised delivery date, substitution, or full refund for the
            affected items.
          </p>
        </Section>

        <Section id="limitation-of-liability" number={11} title="Limitation of Liability" delay={0.02}>
          <Clause number="11.1">
            Subject to Section 11.2, the Supplier&apos;s total liability to the Buyer arising out of or in
            connection with an order, whether in contract, tort, or otherwise, is limited to the price paid by
            the Buyer for that order.
          </Clause>
          <Clause number="11.2">
            Nothing in these Terms excludes or limits the Supplier&apos;s liability for death or personal
            injury caused by proven negligence (including in relation to food safety), fraud or fraudulent
            misrepresentation, or any liability that cannot be excluded or limited under applicable Indian law,
            including the Consumer Protection Act, 2019 and the Food Safety and Standards Act, 2006.
          </Clause>
          <Clause number="11.3">The Supplier is not liable for indirect or consequential loss, subject to Section 11.2.</Clause>
        </Section>

        <Section id="indemnity" number={12} title="Indemnity" delay={0.02}>
          <p>
            The Buyer agrees to indemnify the Supplier against reasonable losses, costs, or claims arising from
            the Buyer&apos;s breach of these Terms, misuse of the Platform, or provision of inaccurate delivery
            or account information, except to the extent caused by the Supplier&apos;s own negligence or
            breach.
          </p>
        </Section>

        <Section id="intellectual-property" number={13} title="Intellectual Property" delay={0.02}>
          <p>
            All content on the Platform, including text, graphics, logos, and the &quot;{COMPANY_NAME}&quot;
            brand name and marks, is owned by or licensed to the Supplier and may not be copied, reproduced, or
            used without prior written consent, except as necessary to use the Platform for its intended
            purpose.
          </p>
        </Section>

        <Section id="grievance-redressal" number={14} title="Grievance Redressal" delay={0.02}>
          <p>
            In accordance with the Information Technology Act, 2000, the Consumer Protection Act, 2019, and
            the Consumer Protection (E-Commerce) Rules, 2020, the Supplier has appointed a Grievance Officer to
            address Buyer complaints regarding these Terms and orders placed on the Platform.
          </p>
          <div className="mt-4 rounded-2xl bg-foam-100 p-5 text-sm dark:bg-white/5">
            <p>
              <span className="font-semibold text-ocean-950 dark:text-foam-50">Grievance Officer:</span> {GRIEVANCE_OFFICER_NAME}
            </p>
            <p className="mt-1.5">
              <span className="font-semibold text-ocean-950 dark:text-foam-50">Email:</span>{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-teal-600 hover:text-teal-700 dark:text-teal-300">
                {CONTACT_EMAIL}
              </a>
            </p>
            <p className="mt-1.5">
              <span className="font-semibold text-ocean-950 dark:text-foam-50">Phone:</span>{" "}
              <a href={`tel:${CONTACT_PHONE_HREF}`} className="text-teal-600 hover:text-teal-700 dark:text-teal-300">
                {CONTACT_PHONE_DISPLAY}
              </a>
            </p>
            <p className="mt-1.5">
              <span className="font-semibold text-ocean-950 dark:text-foam-50">Address:</span> {REGISTERED_ADDRESS}
            </p>
          </div>
          <p className="mt-4">
            Complaints will be acknowledged within 48 hours and, wherever possible, resolved within 30 days of
            receipt. If the Buyer is not satisfied with the outcome, they may approach the National Consumer
            Helpline, file a complaint on the government&apos;s e-daakhil portal, or approach the appropriate
            Consumer Disputes Redressal Commission under the Consumer Protection Act, 2019.
          </p>
        </Section>

        <Section id="amendments" number={15} title="Amendments" delay={0.02}>
          <p>
            The Supplier may update these Terms from time to time to reflect changes in its practices,
            offerings, or applicable law. Material changes will be notified through the Platform, and the
            &quot;Last Updated&quot; date above will be revised accordingly. Continued use of the Platform
            after changes take effect constitutes acceptance of the revised Terms; changes will not apply
            retrospectively to orders already confirmed.
          </p>
        </Section>

        <Section id="governing-law" number={16} title="Governing Law and Jurisdiction" delay={0.02}>
          <p>
            These Terms are governed by the laws of India. Subject to the Buyer&apos;s statutory rights to
            approach a Consumer Disputes Redressal Commission having jurisdiction over their place of
            residence, any other disputes arising out of or in connection with these Terms are subject to the
            exclusive jurisdiction of the courts at {JURISDICTION_CITY}.
          </p>
        </Section>

        <Section id="general" number={17} title="General" delay={0.02}>
          <Clause number="17.1 Severability.">
            If any provision of these Terms is held invalid or unenforceable, the remaining provisions will
            continue in full force.
          </Clause>
          <Clause number="17.2 Entire agreement.">
            These Terms, together with the Privacy Policy and any Order Confirmation, constitute the entire
            agreement between the Supplier and the Buyer for that order.
          </Clause>
          <Clause number="17.3 Notices.">
            Notices to the Buyer will be sent to the email, phone number, or in-app address on their account;
            notices to the Supplier should be sent to the contact details in Section 14.
          </Clause>
          <Clause number="17.4 Assignment.">
            The Buyer may not assign or transfer their rights under these Terms without the Supplier&apos;s
            prior written consent. The Supplier may assign these Terms in connection with a merger,
            acquisition, or sale of assets, with notice to the Buyer.
          </Clause>
        </Section>

        <Section id="contact-us" number={18} title="Contact Us" delay={0.02}>
          <div className="mt-1 rounded-2xl bg-foam-100 p-5 text-sm dark:bg-white/5">
            <p className="font-display font-semibold text-ocean-950 dark:text-foam-50">{COMPANY_NAME}</p>
            <p className="mt-1.5">{REGISTERED_ADDRESS}</p>
            <p className="mt-1.5">
              Email:{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-teal-600 hover:text-teal-700 dark:text-teal-300">
                {CONTACT_EMAIL}
              </a>
            </p>
            <p className="mt-1.5">
              Phone:{" "}
              <a href={`tel:${CONTACT_PHONE_HREF}`} className="text-teal-600 hover:text-teal-700 dark:text-teal-300">
                {CONTACT_PHONE_DISPLAY}
              </a>
            </p>
          </div>
        </Section>
      </section>
    </PageTransition>
  );
}

/** /terms: the version staff published (admin → Policy & legal), else the built-in Terms above. */
export default function TermsAndConditions() {
  const published = usePublishedPolicy("terms");
  if (published.status === "loading") return <PolicyLoading />;
  return published.status === "ready" ? <PolicyPage doc={published.doc} /> : <BuiltInTerms />;
}
